import { AppCoordinator } from "./core/AppCoordinator";
import {
	createError,
	ErrorSeverity,
	ErrorType,
	errorHandler,
	errorUI,
} from "./error-handling";
import {
	type SessionControlCallbacks,
	SessionControlService,
} from "./services/session-control-service";
import {
	type SettingsCallbacks,
	SettingsDataService,
} from "./services/settings-data-service";
import type {
	Comment,
	CommenterDefinition,
	TranscriptSegment as CoreTranscriptSegment,
	Session,
	SessionSummary,
} from "./types/core";
import type { TranscriptSegment as UITranscriptSegment } from "./ui/components/TranscriptionDisplay";

export interface AppCallbacks {
	onStatusChange: (
		message: string,
		type: "ready" | "running" | "error",
	) => void;
	onTranscriptionUpdate: (segment: UITranscriptSegment) => void;
	onTranscriptionBulkUpdate: (segments: UITranscriptSegment[]) => void;
	onTranscriptionClear: () => void;
	onCommentsUpdate: (comments: Comment[]) => void;
	onSessionStateChange: (isRunning: boolean, isPaused: boolean) => void;
	onCommenterDefinitionsUpdate: (definitions: CommenterDefinition[]) => void;
	onLowConfidenceThresholdUpdate: (threshold: number) => void;
	onSummaryGenerated: (summary: SessionSummary, session: Session) => void;
	onSessionIdChange: (sessionId: string | null) => void;
	onAutoResume?: () => void;
}

export class MainService {
	private coordinator: AppCoordinator;
	private callbacks: AppCallbacks;
	private sessionControlService: SessionControlService;
	private settingsDataService: SettingsDataService;

	/**
	 * Compatibility getters for tests that access private members via (app as any).
	 * These are marked public to satisfy tsc's noUnusedLocals but are intended for internal/test use only.
	 */
	public get storage() {
		return this.coordinator.storage;
	}
	public get voiceManager() {
		return this.coordinator.voiceManager;
	}
	public get commentSystem() {
		return this.coordinator.commentSystem;
	}
	public get preferenceManager() {
		return this.coordinator.preferenceManager;
	}
	public get summaryGenerator() {
		return this.coordinator.summaryGenerator;
	}
	public get sessionManager() {
		return this.coordinator.sessionManager;
	}
	public get audioManager() {
		return this.coordinator.audioManager;
	}
	public get audioRecorder() {
		return this.coordinator.audioRecorder;
	}
	public get audioAnalyzer() {
		return this.coordinator.audioAnalyzer;
	}
	public get presetManager() {
		return this.coordinator.presetManager;
	}
	public get isRunning() {
		return this.coordinator.isRunning;
	}
	public get isPaused() {
		return this.coordinator.isPaused;
	}
	public get currentSessionId() {
		return this.coordinator.currentSessionId;
	}

	constructor(callbacks: AppCallbacks) {
		this.callbacks = callbacks;
		this.coordinator = new AppCoordinator({
			...callbacks,
			onAutoResume: () => {
				this.resumeSession();
				if (callbacks.onAutoResume) {
					callbacks.onAutoResume();
				}
			},
		});
		this.sessionControlService = new SessionControlService(
			this.coordinator,
			this.callbacks as SessionControlCallbacks,
			(isStartup) => this.checkApiKeys(isStartup),
		);
		this.settingsDataService = new SettingsDataService(
			this.coordinator,
			this.callbacks as SettingsCallbacks,
		);
	}

	async initialize(): Promise<void> {
		// Set up global error handlers
		this.setupGlobalErrorHandlers();
		await this.coordinator.initialize();

		// Additional check for API keys after initialization
		this.checkApiKeys(true);
	}

	/**
	 * サービス全体を停止し、リソースを解放する
	 */
	async destroy(): Promise<void> {
		await this.coordinator.destroy();
	}

	private setupGlobalErrorHandlers(): void {
		for (const type of Object.values(ErrorType)) {
			errorHandler.onError(type as ErrorType, (error) => {
				errorUI.showError(error, errorHandler.getRecoveryStrategy(error));
			});
		}
	}

	async startSession(): Promise<void> {
		await this.sessionControlService.startSession();
	}

	pauseSession(): void {
		this.sessionControlService.pauseSession();
	}

	resumeSession(): void {
		this.sessionControlService.resumeSession();
	}

	async resumeExistingSession(sessionId: string): Promise<void> {
		await this.sessionControlService.resumeExistingSession(sessionId);
	}

	async stopSession(): Promise<void> {
		await this.sessionControlService.stopSession();
	}

	async generateComments(): Promise<void> {
		await this.sessionControlService.generateComments();
	}

	handleCommentClick(id: string): void {
		this.sessionControlService.handleCommentClick(id);
	}

	private async checkApiKeys(isStartup = false): Promise<boolean> {
		const prefs = this.coordinator.preferenceManager?.getCurrentPreferences();
		const geminiKey = prefs?.apiKeys?.summaryApiKey;
		const hasGemini = !!geminiKey && geminiKey.trim() !== "";

		if (!hasGemini) {
			await errorHandler.handleError(
				createError(
					ErrorType.CONFIG,
					isStartup ? ErrorSeverity.HIGH : ErrorSeverity.MEDIUM,
					"Gemini APIキーが設定されていません。コメント生成・要約機能は無効になります。",
				),
			);
		}

		return hasGemini;
	}

	// -------------------------------------------------------------------------
	// Preference Management Methods (Delegated to coordinator)
	// -------------------------------------------------------------------------

	getAudioConfig() {
		return this.settingsDataService.getAudioConfig();
	}

	async updateAudioConfig(
		config: Partial<import("./types/core").AudioAnalysisConfig>,
	): Promise<void> {
		await this.settingsDataService.updateAudioConfig(config);
	}

	async updateApiKeys(keys: { gemini?: string }): Promise<void> {
		await this.settingsDataService.updateApiKeys(keys);
	}

	async updateCommentMaxCount(count: number): Promise<void> {
		await this.settingsDataService.updateCommentMaxCount(count);
	}

	async updateNotificationsEnabled(enabled: boolean): Promise<void> {
		await this.settingsDataService.updateNotificationsEnabled(enabled);
	}

	async updateAutoCommentConfig(
		config: Partial<import("./types/core").AutoCommentConfig>,
	): Promise<void> {
		await this.settingsDataService.updateAutoCommentConfig(config);
	}

	async getPreferences() {
		return await this.settingsDataService.getPreferences();
	}

	async updateTranscriptionConfig(threshold: number): Promise<void> {
		await this.settingsDataService.updateTranscriptionConfig(threshold);
	}

	async updatePolicyConfig(policy: string): Promise<void> {
		await this.settingsDataService.updatePolicyConfig(policy);
	}

	async updateCommenterDefinitions(
		definitions: CommenterDefinition[],
	): Promise<void> {
		await this.settingsDataService.updateCommenterDefinitions(definitions);
	}

	async getPresets() {
		return await this.settingsDataService.getPresets();
	}

	async applyPreset(id: string): Promise<void> {
		await this.settingsDataService.applyPreset(id);
	}

	async saveCurrentAsPreset(name: string, description: string): Promise<void> {
		await this.settingsDataService.saveCurrentAsPreset(name, description);
	}

	async deletePreset(id: string): Promise<void> {
		await this.settingsDataService.deletePreset(id);
	}

	async updatePreset(
		id: string,
		updates: { name?: string; description?: string },
	): Promise<void> {
		await this.settingsDataService.updatePreset(id, updates);
	}

	async exportData(): Promise<string> {
		return await this.settingsDataService.exportData();
	}

	async importData(json: string): Promise<void> {
		await this.settingsDataService.importData(json);
	}

	async getHistory(): Promise<Session[]> {
		return await this.settingsDataService.getHistory();
	}

	async deleteSession(sessionId: string): Promise<void> {
		await this.settingsDataService.deleteSession(sessionId);
	}

	async deleteSessions(sessionIds: string[]): Promise<void> {
		await this.settingsDataService.deleteSessions(sessionIds);
	}

	async getAudioFiles(sessionId: string) {
		return await this.settingsDataService.getAudioFiles(sessionId);
	}

	async getSummary(sessionId: string) {
		return await this.settingsDataService.getSummary(sessionId);
	}

	async renameSession(sessionId: string, newTitle: string): Promise<void> {
		await this.settingsDataService.renameSession(sessionId, newTitle);
	}

	async toggleFavoriteSession(
		sessionId: string,
		isFavorite: boolean,
	): Promise<void> {
		await this.settingsDataService.toggleFavoriteSession(sessionId, isFavorite);
	}

	async updateTranscriptSegment(
		sessionId: string,
		segmentId: string,
		newText: string,
	): Promise<void> {
		await this.sessionControlService.updateTranscriptSegment(
			sessionId,
			segmentId,
			newText,
		);
	}

	async deleteTranscriptSegment(
		sessionId: string,
		segmentId: string,
	): Promise<void> {
		await this.sessionControlService.deleteTranscriptSegment(
			sessionId,
			segmentId,
		);
	}

	async updateSessionTranscript(
		sessionId: string,
		transcript: CoreTranscriptSegment[],
	): Promise<void> {
		await this.sessionControlService.updateSessionTranscript(
			sessionId,
			transcript,
		);
	}

	async generateSummaryManual(sessionId: string): Promise<SessionSummary> {
		return await this.sessionControlService.generateSummaryManual(sessionId);
	}
}

export { MainService as MonologLiveApp };
